/**
 * Printable product configurator (single-product-printable.twig).
 *
 * Selecting a pack-size card updates the price, the "Pack size" label, the
 * add-to-cart label, and the hidden variation id / attribute_pack fields the
 * real WooCommerce form posts. The upload slot grid + counter are owned by
 * initPhotoUpload(); on a user-driven pack change this dispatches a
 * `sm:packchange` event so the uploader can resize the set to the new count.
 *
 * Hooks (set in the twig): [data-product-config] (root), [data-pack-card]
 * (+ data-pack-id/-count/-price), [data-pack-label], [data-price-display],
 * [data-add-to-cart], [data-variation-id], [data-pack-attr].
 */

/**
 * Wire the configurator: keep every label in sync with the selected pack card.
 * Safe to call on pages without a configurator (returns early).
 */
export function initProductConfigurator() {
  const root = document.querySelector('[data-product-config]');
  if (!root) {
    return;
  }

  const cards = root.querySelectorAll('[data-pack-card]');
  if (!cards.length) {
    return;
  }

  const packLabel = root.querySelector('[data-pack-label]');
  const priceDisplay = root.querySelector('[data-price-display]');
  const addBtn = root.querySelector('[data-add-to-cart]');
  const variationInput = root.querySelector('[data-variation-id]');
  const packAttrInput = root.querySelector('[data-pack-attr]');

  /** Apply a card's data to every dependent label + the hidden form fields. */
  const selectPack = (card, isUserAction) => {
    const count = parseInt(card.dataset.count, 10) || 0;
    const price = card.dataset.price || '';

    cards.forEach((c) => {
      c.removeAttribute('data-selected');
      c.setAttribute('aria-pressed', 'false');
    });
    card.setAttribute('data-selected', '');
    card.setAttribute('aria-pressed', 'true');

    if (packLabel) {
      // The translated template rides in on data-template (set in the twig);
      // hardcoding English here would undo the server-side translation.
      const template = packLabel.dataset.template || 'Pack size: %d magnets';
      packLabel.textContent = template.replace('%d', String(count));
    }
    if (priceDisplay) {
      priceDisplay.textContent = price;
    }
    if (addBtn) {
      addBtn.dataset.priceLabel = price;
    }
    if (variationInput) {
      variationInput.value = card.dataset.packId || '';
    }
    if (packAttrInput) {
      packAttrInput.value = String(count);
    }

    // The uploader owns the slot grid + counter + add-to-cart enablement.
    if (isUserAction) {
      root.dispatchEvent(new CustomEvent('sm:packchange', { detail: { count, packId: card.dataset.packId || '', price } }));
    }
  };

  cards.forEach((card) => {
    card.addEventListener('click', () => {
      // Ignore pack changes while an AJAX add-to-cart is committing the current
      // set: resizing would delete staged files the server is attaching to the
      // cart line and repaint the button out of its "Adding…" state.
      if (addBtn && addBtn.dataset.loading !== undefined) {
        return;
      }
      selectPack(card, true);
    });
  });

  // Preselect a pack passed in the URL (the homepage Product List cards link with
  // ?attribute_pack=<count>; ?variation/?variation_id are also honored). Falls
  // back to the server-rendered default. The uploader inits after this and reads
  // the selected card, so no event is needed.
  const params = new URLSearchParams(window.location.search);
  const wantVariation = params.get('variation') || params.get('variation_id');
  const wantPack = params.get('attribute_pack');
  let fromUrl = null;
  if (wantVariation) {
    fromUrl = Array.from(cards).find((c) => c.dataset.packId === String(wantVariation));
  }
  if (!fromUrl && wantPack) {
    fromUrl = Array.from(cards).find((c) => c.dataset.count === String(wantPack));
  }

  const initial = fromUrl || root.querySelector('[data-pack-card][data-selected]') || cards[0];
  selectPack(initial, false);
}

/**
 * Product image gallery (views/partial/woo-gallery.twig): clicking a thumbnail
 * swaps the main image; clicking the main image opens a full-screen lightbox
 * with prev/next, Escape-to-close, arrow keys, and focus restore. Safe to call
 * on pages without a gallery (returns early).
 */
export function initProductGallery() {
  const root = document.querySelector('[data-product-gallery]');
  if (!root) {
    return;
  }

  const mainImg = root.querySelector('[data-gallery-main-img]');
  const mainBtn = root.querySelector('[data-gallery-main]');
  const thumbs = Array.from(root.querySelectorAll('[data-gallery-thumb]'));

  const images = thumbs.length
    ? thumbs.map((t) => ({ main: t.dataset.main, full: t.dataset.full, alt: t.dataset.alt || '' }))
    : mainImg
      ? [{ main: mainImg.src, full: mainImg.src, alt: mainImg.alt }]
      : [];
  if (!images.length) {
    return;
  }

  let current = 0;

  /** Make thumbnail `i` the active one: swap the main image and toggle the selected/aria state across the rail. */
  const selectThumb = (i) => {
    const img = images[i];
    if (!img) {
      return;
    }
    current = i;
    if (mainImg) {
      mainImg.src = img.main;
      mainImg.alt = img.alt;
    }
    thumbs.forEach((t, j) => {
      const on = j === i;
      if (on) {
        t.setAttribute('data-selected', '');
      } else {
        t.removeAttribute('data-selected');
      }
      t.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  };

  thumbs.forEach((t, i) => t.addEventListener('click', () => selectThumb(i)));

  // Thumbnail carousel: on desktop the rail shows a fixed window of VISIBLE
  // thumbnails and the arrows page the track via translateY; on mobile the rail
  // scrolls horizontally and the arrows are hidden.
  const track = root.querySelector('[data-thumb-track]');
  const viewport = root.querySelector('[data-thumb-viewport]');
  const prevArrow = root.querySelector('[data-thumb-prev]');
  const nextArrow = root.querySelector('[data-thumb-next]');
  if (track && viewport && prevArrow && nextArrow && thumbs.length > 1) {
    const VISIBLE = 4;
    const VGAP = 10; // desktop vertical track gap (gap-[10px])
    const HGAP = 12; // mobile horizontal track gap (gap-3)
    const maxIndex = Math.max(0, thumbs.length - VISIBLE);
    const desktop = window.matchMedia('(min-width: 1000px)');
    const slots = Math.min(VISIBLE, thumbs.length);
    let index = 0;

    /**
     * Lay out and page the thumbnail rail for the current breakpoint: size the
     * thumbnails so VISIBLE of them plus the arrows match the main image,
     * translate the track to `index`, and clamp/toggle the arrows.
     */
    const applyRail = () => {
      if (index > maxIndex) {
        index = maxIndex;
      }
      if (index < 0) {
        index = 0;
      }

      if (desktop.matches) {
        // Vertical: size thumbnail HEIGHT so 4 thumbnails + the two arrows
        // equal the main image height; page with translateY.
        const h = Math.min(320, Math.max(40, Math.floor((viewport.clientHeight - (slots - 1) * VGAP) / slots)));
        thumbs.forEach((t) => {
          t.style.width = '';
          t.style.height = `${h}px`;
        });
        track.style.transform = maxIndex > 0 ? `translateY(${-index * (h + VGAP)}px)` : '';
      } else {
        // Horizontal: size thumbnail WIDTH so 4 thumbnails + the two arrows
        // equal the main image width; page with translateX.
        const w = Math.max(48, Math.floor((viewport.clientWidth - (slots - 1) * HGAP) / slots));
        thumbs.forEach((t) => {
          t.style.height = '';
          t.style.width = `${w}px`;
        });
        track.style.transform = maxIndex > 0 ? `translateX(${-index * (w + HGAP)}px)` : '';
      }

      const hasOverflow = maxIndex > 0;
      prevArrow.style.display = hasOverflow ? '' : 'none';
      nextArrow.style.display = hasOverflow ? '' : 'none';
      prevArrow.disabled = index <= 0;
      nextArrow.disabled = index >= maxIndex;
    };

    prevArrow.addEventListener('click', () => {
      index -= VISIBLE;
      applyRail();
    });
    nextArrow.addEventListener('click', () => {
      index += VISIBLE;
      applyRail();
    });
    window.addEventListener('resize', applyRail);
    requestAnimationFrame(applyRail);
  }

  const lightbox = root.querySelector('[data-gallery-lightbox]');
  const lightboxImg = lightbox && lightbox.querySelector('[data-gallery-lightbox-img]');
  if (!lightbox || !lightboxImg || !mainBtn) {
    return;
  }

  // Move the lightbox to <body> so its fixed overlay lives in the root stacking
  // context. Inside the gallery's position:sticky wrapper it forms a nested
  // stacking context, which would trap its z-index below the sticky header.
  document.body.appendChild(lightbox);

  const closeBtn = lightbox.querySelector('[data-gallery-close]');
  const prevBtn = lightbox.querySelector('[data-gallery-prev]');
  const nextBtn = lightbox.querySelector('[data-gallery-next]');
  let lastFocused = null;

  /** True while the lightbox overlay is visible. */
  const isOpen = () => !lightbox.classList.contains('hidden');

  /** Show image `i` in the lightbox (wrapping around the ends) and sync the rail behind it. */
  const showAt = (i) => {
    current = ((i % images.length) + images.length) % images.length;
    lightboxImg.src = images[current].full;
    lightboxImg.alt = images[current].alt;
    selectThumb(current); // keep the main image + rail in sync behind the lightbox
  };

  /** Open the lightbox at the current image, lock body scroll, and remember focus for restore. */
  const openLightbox = () => {
    lastFocused = document.activeElement;
    showAt(current);
    lightbox.classList.remove('hidden');
    lightbox.classList.add('flex');
    document.body.style.overflow = 'hidden';
    if (closeBtn) {
      closeBtn.focus();
    }
  };

  /** Hide the lightbox, restore body scroll, and return focus to the opener. */
  const closeLightbox = () => {
    lightbox.classList.add('hidden');
    lightbox.classList.remove('flex');
    document.body.style.overflow = '';
    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
    }
  };

  mainBtn.addEventListener('click', openLightbox);
  if (closeBtn) {
    closeBtn.addEventListener('click', closeLightbox);
  }
  if (prevBtn) {
    prevBtn.addEventListener('click', () => showAt(current - 1));
  }
  if (nextBtn) {
    nextBtn.addEventListener('click', () => showAt(current + 1));
  }
  // Click on the backdrop (not the image or controls) closes.
  lightbox.addEventListener('click', (event) => {
    if (event.target === lightbox) {
      closeLightbox();
    }
  });
  document.addEventListener('keydown', (event) => {
    if (!isOpen()) {
      return;
    }
    if (event.key === 'Escape') {
      closeLightbox();
    } else if (event.key === 'ArrowLeft' && prevBtn) {
      showAt(current - 1);
    } else if (event.key === 'ArrowRight' && nextBtn) {
      showAt(current + 1);
    }
  });

  // Trap Tab focus inside the open lightbox (it is a modal dialog).
  const LIGHTBOX_FOCUSABLE = 'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])';
  lightbox.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab' || !isOpen()) {
      return;
    }
    const focusables = Array.from(lightbox.querySelectorAll(LIGHTBOX_FOCUSABLE)).filter((el) => el.offsetParent !== null);
    if (!focusables.length) {
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });
}

/**
 * Replace the native number-input spinners on the regular product page with a
 * themed [-] value [+] stepper. Progressive enhancement: the input stays a
 * working number field if this never runs. Safe to call on pages without one.
 */
export function initQuantitySteppers() {
  document
    .querySelectorAll('.sm-woo-purchase .quantity input.qty, .sm-cart-form .quantity input.qty')
    .forEach(enhanceQty);
}

/** Wrap a single WooCommerce quantity input with minus/plus stepper buttons. */
function enhanceQty(input) {
  // Skip locked inputs: sold-individually (printable) cart lines render a
  // readonly "1" that must stay a static, non-interactive value.
  if (input.readOnly || input.disabled) {
    return;
  }
  const box = input.closest('.quantity');
  if (!box || box.classList.contains('sm-qty')) {
    return;
  }
  box.classList.add('sm-qty');

  const step = parseFloat(input.getAttribute('step')) || 1;
  const min = input.getAttribute('min') !== null && input.getAttribute('min') !== '' ? parseFloat(input.getAttribute('min')) : 1;
  const maxAttr = input.getAttribute('max');
  const max = maxAttr !== null && maxAttr !== '' ? parseFloat(maxAttr) : Infinity;

  /** Create a non-focusable stepper button; the glyph is drawn in CSS via `modifier`. */
  const makeBtn = (label, modifier) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `sm-qty-btn ${modifier}`;
    btn.setAttribute('aria-label', label);
    btn.tabIndex = -1; // the input remains the keyboard target
    return btn; // the - / + glyphs are drawn in CSS (pixel-centered bars)
  };

  const minus = makeBtn('Decrease quantity', 'sm-qty-minus');
  const plus = makeBtn('Increase quantity', 'sm-qty-plus');

  /** Current numeric value of the input, falling back to `min` when blank/NaN. */
  const current = () => {
    const value = parseFloat(input.value);
    return Number.isNaN(value) ? min : value;
  };

  /** Enable/disable the minus and plus buttons against the min/max bounds. */
  const sync = () => {
    minus.disabled = current() <= min;
    plus.disabled = current() >= max;
  };

  /** Clamp `next` to the min/max range, write it back, and fire input/change so Woo updates. */
  const setValue = (next) => {
    input.value = String(Math.max(min, Math.min(max, next)));
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
    sync();
  };

  minus.addEventListener('click', () => setValue(current() - step));
  plus.addEventListener('click', () => setValue(current() + step));
  input.addEventListener('input', sync);
  input.addEventListener('change', sync);

  box.insertBefore(minus, input);
  input.insertAdjacentElement('afterend', plus);
  sync();
}

/**
 * Cart auto-update + themed quantity steppers, resilient to WooCommerce's AJAX
 * cart refresh.
 *
 * Classic WooCommerce needs an "Update cart" submit for a quantity change to
 * persist. To match the design (no visible button) we hide it (.sm-cart-live)
 * and, on a debounced quantity change, click the hidden button. WooCommerce
 * intercepts that click and refreshes the cart via AJAX - and crucially it
 * `replaceWith()`s the whole .woocommerce-cart-form, which drops our themed
 * [- +] steppers (the inputs fall back to the native number spinners), the
 * .sm-cart-live class, and any listener bound to the old form.
 *
 * So: the change listener is delegated on the stable .sm-cart section (it
 * survives the form swap), and a MutationObserver re-themes the cart after every
 * refresh. The observer callback runs at microtask timing - before the browser
 * paints - so there is no flash of the native spinners. Safe on non-cart pages.
 */
export function initCart() {
  const section = document.querySelector('.sm-cart');
  if (!section || !section.querySelector('.sm-cart-form')) {
    return;
  }

  let observer = null;

  // Re-theme the (possibly just-replaced) cart form: hide the native update
  // button and swap the quantity inputs for our steppers. Disconnect while we
  // mutate so our own DOM insertions don't re-trigger the observer.
  const theme = () => {
    if (observer) {
      observer.disconnect();
    }
    const form = section.querySelector('.sm-cart-form');
    if (form) {
      form.classList.add('sm-cart-live');
    }
    initQuantitySteppers();
    if (observer) {
      observer.observe(section, { childList: true, subtree: true });
    }
  };

  // Debounced auto-submit on quantity change. Delegated on the stable section so
  // it keeps working after WooCommerce replaces the form.
  let timer = null;
  section.addEventListener('change', (event) => {
    const target = event.target;
    if (!target || !target.classList || !target.classList.contains('qty')) {
      return;
    }
    clearTimeout(timer);
    timer = setTimeout(() => {
      const update = section.querySelector('.sm-cart-form [name="update_cart"]');
      if (update) {
        update.disabled = false;
        update.click(); // WooCommerce intercepts this and refreshes the cart via AJAX
      }
    }, 650);
  });

  observer = new MutationObserver(theme);
  theme();
}
